import { EventEmitter, inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivationEnd,
  NavigationEnd,
  NavigationExtras,
  NavigationStart,
  Params,
  Router,
} from '@angular/router';
import { ISearchQuery, PersonProfile, SearchType } from 'aa-ng';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';

import {
  IDashboardParams,
  IDashboardState,
  IEdsInstitution,
  IInstitutionContext,
  IInstitutionUnit,
  InstitutionAbbrNavOverrideMap,
  InstitutionNameOverrideMap,
  ISelectableUnit,
  IUnitDisplayType,
  NavigationTier,
  PageEnum,
  SelectableUnitGroup,
  SystemAbbrNavOverrideMap,
  UnitType,
} from '../../models';
import { sortByStringProperty } from '../helpers/sort-by-string-property';
import { titleCase } from '../helpers/title-case';
import { InstitutionsService } from './institutions.service';
import { SEOService } from './seo.service';
import { UnitsService } from './units.service';

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private subscriptions: Subscription[];

  private router = inject(Router);
  private seoService = inject(SEOService);
  private institutionsService = inject(InstitutionsService);
  private unitsService = inject(UnitsService);
  private route = inject(ActivatedRoute);

  private activatedRoute: ActivatedRoute;
  private dataLoading: boolean;
  private lastKnownUrl = '';
  private context: IInstitutionContext;

  isSystem: boolean;
  dashboardSiteName: string;
  dashboardSecondarySiteName: string;
  dashboardTitle: string;
  dashboardAbbreviation: string;
  dashboardLogoPath: string;
  dashboardNavigationLevel: NavigationTier;
  dashboardAbbr: string;

  systemLogoPath: string;

  // Events
  unitNavigationEvent = new EventEmitter();

  // Standard Data
  params = new BehaviorSubject<IDashboardParams>({});
  searchParams = new BehaviorSubject<ISearchQuery>({ type: SearchType.Topic });
  pageGroup = new BehaviorSubject<PageEnum>(null);
  institutions = new BehaviorSubject<ISelectableUnit[]>([]);
  units = new BehaviorSubject<ISelectableUnit[]>([]);
  unitTypes = new BehaviorSubject<IUnitDisplayType[]>([]);
  private _state = new BehaviorSubject<IDashboardState>(null);
  state = this._state.asObservable().pipe(filter((x) => !!x));

  // UI Specific Data for Menus
  selectedUnitType = new BehaviorSubject<IUnitDisplayType>(null);
  displayUnits = new BehaviorSubject<ISelectableUnit[]>([]);
  selectedDisplayUnit = new BehaviorSubject<ISelectableUnit>(null);

  // UI Specific Data for Mega Menu
  displayUnitTree = new BehaviorSubject<SelectableUnitGroup[]>([]);

  profileSubject = new BehaviorSubject<PersonProfile>(null);
  dashboardShortName$ = new BehaviorSubject<string>(null);

  get dashboardShortName() {
    return this.dashboardShortName$.value;
  }

  set dashboardShortName(val: string) {
    this.dashboardShortName$.next(val);
  }
  constructor() {
    this.subscriptions = [
      // Recieve and parse Params
      this.route.data.subscribe(() => {
        if (
          this.route &&
          this.route.snapshot &&
          this.route.snapshot.queryParams
        ) {
          const params: Params = this.route.snapshot.queryParams;
          this.parseParams(params);
        }
      }),
      // Recieve all aspects of the institution context
      this.institutionsService.context.subscribe((context) => {
        this.context = context;
        this.dashboardSecondarySiteName = context.secondarySiteName;
        if (context.institutionAbbreviation) {
          if (context.institutionAbbreviation.length > 4) {
            context.institutionAbbreviation = titleCase(
              context.institutionAbbreviation,
            );
          } else {
            context.institutionAbbreviation =
              context.institutionAbbreviation.toUpperCase();
          }
        }

        this.isSystem = Boolean(
          context.institutionGroup && context.institutionGroup.institutions,
        );
        const unitTypes =
          context.dashboardUnitTypes ?
            context.dashboardUnitTypes.map((x: number) => ({
              type: x,
              name:
                context.dashboardUnitNameOverride?.[x] ?
                  context.dashboardUnitNameOverride[x]
                : x in UnitType ? UnitType[x]
                : '',
            }))
          : [];
        if (this.isSystem) {
          this.institutions.next(
            context.institutionGroup.institutions
              .map((x: IEdsInstitution) => ({
                id: x.id,
                name: x.name,
                shortName: x.shortName,
                altName:
                  InstitutionNameOverrideMap.has(x.id) ?
                    InstitutionNameOverrideMap.get(x.id)
                  : x.name,
                abbreviation:
                  x.abbreviation ? x.abbreviation.toUpperCase() : '',
                logoPath: x.logoPath,
                tier: NavigationTier.Institution,
                url: [''],
              }))
              .sort(
                sortByStringProperty((d) => (d.altName ? d.altName : d.name)),
              ),
          );
        }

        if (unitTypes && unitTypes.length > 0) {
          this.selectedUnitType.next(unitTypes[0]);
          this.unitTypes.next(unitTypes);
          this.dataLoading = true;
          this.unitsService
            .getUnitsFromManyInstitutions(
              (
                context.institutionGroup &&
                  context.institutionGroup.institutions
              ) ?
                context.institutionGroup.institutions.map((x) => x.id)
              : [+context.institutionId],
              context.dashboardUnitTypes,
            )
            .pipe(
              map((units: IInstitutionUnit[]) => {
                return units
                  .map(
                    (x) =>
                      <ISelectableUnit>{
                        id: x.unitId,
                        parentId: x.institutionId,
                        name: x.unit ? x.unit.name.trim() : '',
                        shortName: x.unit ? x.unit.baseName.trim() : '',
                        displayName: x.unit ? x.unit.displayName?.trim() : '',
                        type: x.unit ? x.unit.type : 0,
                        tier: NavigationTier.Unit,
                        url: [''],
                        aaId: x.unit.aaid,
                      },
                  )
                  .sort(
                    sortByStringProperty((u) =>
                      (
                        context.showDashboardUnitCollaborationWidget &&
                        u.displayName
                      ) ?
                        u.displayName
                      : u.type === 2 || u.type === 3 ? u.shortName
                      : u.name,
                    ),
                  );
              }),
            )
            .subscribe({
              next: (units: ISelectableUnit[]) => {
                this.dataLoading = false;
                this.units.next(units);
              },
              error: (err) => console.error(err),
            });
        }
      }),
      // Recieve and parse Params
      this.router.events.subscribe((evt) => {
        if (evt instanceof NavigationStart) {
          this.profileSubject.next(null);
        }
        if (evt instanceof ActivationEnd) {
          if (evt.snapshot.data['profile']) {
            this.profileSubject.next(evt.snapshot.data['profile']);
          }
        }
        if (evt instanceof NavigationEnd) {
          const params: Params = this.route.snapshot.queryParams;
          if (evt.url.indexOf('/dashboard') > -1) {
            this.pageGroup.next(PageEnum.Home);
          }
          if (evt.url.indexOf('/search') > -1) {
            this.pageGroup.next(PageEnum.SearchResults);
          }
          if (evt.url.indexOf('/scholar') > -1) {
            this.pageGroup.next(PageEnum.ScholarProfile);
          }
          this.parseParams(params);
        }
      }),
      // Ensure selected UnitType lines up with params
      combineLatest([this.params, this.unitTypes])
        .pipe(
          map(([params, unitTypes]) => {
            if (params && params.unitType && unitTypes) {
              const type = unitTypes.find((x) => x.type === params.unitType);
              if (type && type !== this.selectedUnitType.getValue()) {
                this.selectedUnitType.next(type);
              }
            }
          }),
        )
        .subscribe(),
      // Calculate displayUnits, selectedDisplayUnit
      combineLatest([
        this.institutionsService.context,
        this.params,
        this.searchParams,
        this.pageGroup,
        this.institutions,
        this.units,
        this.unitTypes,
        this.selectedUnitType,
        this.profileSubject,
      ])
        .pipe(
          debounceTime(500),
          map(
            async ([
              context,
              params,
              searchParams,
              pageGroup,
              institutions,
              units,
              unitTypes,
              unitType,
              selectedProfile,
            ]) => {
              if (params && institutions && units && !this.dataLoading) {
                // Ensure that we can continue onward to building and updating dashboard state
                if (!this.ensureStableState(params, units, selectedProfile)) {
                  return;
                }

                // Ensure valid unit type is selected
                if (this.isSystem) {
                  this.updateSelectedUnitType(
                    units,
                    unitTypes,
                    unitType,
                    params,
                  );
                }

                // Generate current state
                const state = this.generateState(
                  context,
                  params,
                  institutions,
                  units,
                  unitType,
                );

                // Setting css class on body for theme
                this.setTheme(context, params, state);

                // Set page title
                this.setTitle(
                  context,
                  searchParams,
                  pageGroup,
                  state,
                  selectedProfile,
                );

                // If state changed, update the observable
                this.updateState(state);
              }
            },
          ),
        )
        .subscribe(),
    ];
  }

  private setTitle(
    context: IInstitutionContext,
    searchParams: ISearchQuery,
    pageGroup: PageEnum,
    state: IDashboardState,
    selectedProfile: PersonProfile,
  ) {
    const instName =
      state.activeInstitutions.length > 1 ?
        context.institutionName
      : state.activeInstitutions[0].name;
    const siteName = context.institutionSiteName;

    switch (pageGroup) {
      case PageEnum.ScholarProfile:
        if (selectedProfile) {
          const name = `${selectedProfile.firstName} ${selectedProfile.lastName}`;
          const unitName =
            selectedProfile.primaryUnitAffiliation ?
              selectedProfile.primaryUnitAffiliation.baseName
            : '';
          this.seoService.setPageTitle(
            `${name}${unitName !== '' ? ' - ' + unitName : ''} | ${instName} | ${siteName}`,
          );
        }
        break;
      case PageEnum.SearchResults:
        if (searchParams && searchParams.query) {
          this.seoService.setPageTitle(
            `Search results for "${searchParams.query}" | ${instName} | ${siteName}`,
          );
        }
        break;
      case PageEnum.Home:
      default:
        if (state.navLevel === NavigationTier.Unit) {
          const unitName = state.activeUnit ? state.activeUnit.name : '';
          this.seoService.setPageTitle(
            `${unitName} | ${instName} | ${siteName}`,
          );
        } else if (state.isSystem && state.navLevel === 0) {
          this.seoService.setPageTitle(`Home - ${siteName}`);
        } else {
          this.seoService.setPageTitle(`Home - ${instName} | ${siteName}`);
        }
        break;
    }
  }

  private setTheme(
    context: IInstitutionContext,
    params: IDashboardParams,
    state: IDashboardState,
  ) {
    this.dashboardSiteName = context.institutionSiteName;
    if (this.isSystem && context.logoPaths && context.logoPaths.length) {
      this.systemLogoPath = context.logoPaths[0];
    }

    if (this.isSystem && params.instId) {
      const inst = state.activeInstitutions[0];
      if (inst) {
        this.dashboardTitle = inst.name;
        this.dashboardShortName =
          context.useCombinedSystemDashboard ?
            context.institutionShortName
          : inst.shortName;
        this.dashboardAbbreviation = inst.abbreviation;
        this.dashboardLogoPath =
          context.useCombinedSystemDashboard ?
            context.logoPaths[0]
          : inst.logoPath;
        this.dashboardAbbr = (
          InstitutionAbbrNavOverrideMap.has(inst.id) ?
            InstitutionAbbrNavOverrideMap.get(inst.id)
          : inst.abbreviation)
          .trim()
          .replace(' ', '')
          .toLowerCase();
        this.toggleBodyTheme(
          `theme-${this.convertToClassName(inst.abbreviation)}`,
        );
      }
    } else {
      this.dashboardTitle = context.institutionSiteName;
      this.dashboardShortName = context.institutionShortName;
      this.dashboardAbbreviation = context.institutionAbbreviation;
      this.dashboardLogoPath =
        context.logoPaths && context.logoPaths.length ?
          context.logoPaths[0]
        : null;
      this.dashboardAbbr = (
        this.isSystem && SystemAbbrNavOverrideMap.has(+context.institutionId) ?
          SystemAbbrNavOverrideMap.get(+context.institutionId)
        : context.institutionAbbreviation)
        .trim()
        .replace(' ', '')
        .toLowerCase();
      this.toggleBodyTheme(
        `theme-${this.convertToClassName(context.institutionAbbreviation)}`,
      );
    }
  }

  private ensureStableState(
    params: IDashboardParams,
    units: ISelectableUnit[],
    selectedProfile: PersonProfile,
  ): boolean {
    // Ensure proper unit header when on profile
    if (selectedProfile && selectedProfile.primaryUnitAffiliation) {
      const unit = units.find(
        (x) =>
          x.id === selectedProfile.primaryUnitAffiliation.id &&
          x.type === selectedProfile.primaryUnitAffiliation.type,
      );
      if (unit && params.unitId !== unit.id) {
        this.selectUnit(
          Object.assign(Object.assign({}, unit), {
            url: null,
            replaceUrl: true,
          }),
        );
        return false;
      }
    }
    // Ensure search params are left behind when leaving search section
    const urlFragment = `${window.location.pathname}${window.location.search}`;
    if (
      this.lastKnownUrl.indexOf('/search') > -1 &&
      urlFragment.indexOf('/search') === -1
    ) {
      const tempParams = this.route.snapshot.queryParams;
      if (tempParams && tempParams['query']) {
        const newParams = Object.assign({}, tempParams);
        delete newParams['query'];
        delete newParams['searchType'];
        delete newParams['institutionIds'];
        this.lastKnownUrl = urlFragment;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: newParams,
          replaceUrl: true,
        });
        return false;
      }
    }
    this.lastKnownUrl = urlFragment;
    return true;
  }

  private generateState(
    context: IInstitutionContext,
    params: IDashboardParams,
    institutions: ISelectableUnit[],
    units: ISelectableUnit[],
    unitType: IUnitDisplayType,
  ) {
    // Set active institutions and units
    let activeInstitutions: ISelectableUnit[] = [];
    if (this.isSystem) {
      activeInstitutions =
        params.instId ?
          institutions.filter((x) => x.id === params.instId)
        : institutions;
    } else {
      activeInstitutions = [
        {
          id: +context.institutionId,
          name: context.institutionName,
          tier: NavigationTier.Institution,
        },
      ];
    }
    const activeUnit: ISelectableUnit =
      params.unitId ?
        units.find(
          (x) => x.id === params.unitId && x.type === params.unitType,
        ) ?? null
      : null;
    // Set nav level
    const navLevel =
      this.isSystem && activeInstitutions.length !== 1 ?
        NavigationTier.System
      : NavigationTier.Institution;
    this.dashboardNavigationLevel =
      activeUnit !== null ? NavigationTier.Unit : navLevel;

    return <IDashboardState>{
      isSystem: this.isSystem,
      navLevel: this.dashboardNavigationLevel,
      activeInstitutions: activeInstitutions,
      activeUnit: activeUnit,
      activeUnitType: unitType,
      activeInstitutionGroupId: context.institutionGroupId,
    };
  }

  private updateState(newState: IDashboardState) {
    const currentState = this._state.getValue();

    if (
      !currentState ||
      JSON.stringify(currentState) !== JSON.stringify(newState)
    ) {
      this._state.next(newState);
    }
  }

  private parseParams(externalParams: Params) {
    const oldParams = this.params.getValue();
    const params: IDashboardParams = {
      instId: externalParams['instId'] ? +externalParams['instId'] : null,
      unitId: externalParams['unitId'] ? +externalParams['unitId'] : null,
      unitType: externalParams['unitType'] ? +externalParams['unitType'] : null,
    };
    if (
      !oldParams ||
      Object.keys(params).some(
        (key) =>
          params[key as keyof IDashboardParams] !==
          oldParams[key as keyof IDashboardParams],
      )
    ) {
      this.params.next(params);
    }

    const oldSearchParams = this.searchParams.getValue();
    const searchParams: ISearchQuery = {
      query: externalParams['query'] ? externalParams['query'] : null,
      type: +SearchType[externalParams['searchType']],
      includedInstitutionIds:
        externalParams['institutionIds'] ?
          Array.isArray(externalParams['institutionIds']) ?
            externalParams['institutionIds'].map((inst) => +inst)
          : [externalParams['institutionIds']]
        : [],
    };
    if (
      !oldSearchParams ||
      Object.keys(searchParams).some(
        (key: string) =>
          searchParams[key as keyof ISearchQuery] !==
          oldSearchParams[key as keyof ISearchQuery],
      )
    ) {
      this.searchParams.next(searchParams);
    }
  }

  private convertToClassName(input: string) {
    return input.replace(/[^a-z0-9]/g, (s) => {
      const c = s.charCodeAt(0);
      if (c === 32 || c === 45) {
        return '-';
      }
      if (c >= 65 && c <= 90) {
        return s.toLowerCase();
      }
      return '';
    });
  }

  private toggleBodyTheme(themeName: string) {
    const list = document.body.classList;
    const fragment = 'theme-';
    const classes: string[] = Array.from(list).filter(
      (x) => x.slice(0, fragment.length) === fragment && x !== themeName,
    );
    list.add(themeName);
    if (classes && classes.length > 0) {
      list.remove(...classes);
    }
  }

  selectUnit(unit: ISelectableUnit) {
    if (
      unit.tier === NavigationTier.Ignore ||
      (this.context.useCombinedSystemDashboard &&
        unit.tier === NavigationTier.Institution) ||
      (this.context.unitBlacklist?.length &&
        this.context.unitBlacklist.includes(unit.id))
    ) {
      return;
    }
    const params: IDashboardParams = {};
    const path: (string | number)[] = unit.url || [];
    if (
      unit.tier === NavigationTier.Institution ||
      unit.tier === NavigationTier.System
    ) {
      params.instId = unit.id;
      params.unitId = undefined;
      params.unitType = undefined;
    } else if (unit.tier === NavigationTier.Unit) {
      if (this.isSystem) {
        params.instId = unit.parentId;
      }
      params.unitId = unit.id;
      params.unitType = unit.id ? unit.type : undefined;
    } else if (unit.tier === NavigationTier.Scholar) {
      path.push(
        ...['/scholar', unit.id, unit.name.replace(' ', '-').toUpperCase()],
      );
    }

    this.navigate(path, params, unit.replaceUrl);
    this.unitNavigationEvent.emit(unit);
  }

  navigate(
    url: (string | number)[] = [],
    params: IDashboardParams = null,
    replaceUrl: boolean = null,
  ) {
    const options: NavigationExtras = {
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl,
    };
    if (url.length === 0) {
      options.relativeTo = this.activatedRoute;
    }
    this.router.navigate(url, options);
  }

  private updateSelectedUnitType(
    units: ISelectableUnit[],
    unitTypes: IUnitDisplayType[],
    selectedUnitType: IUnitDisplayType,
    params: IDashboardParams,
  ) {
    const currentInstUnitTypes = [
      ...new Set(
        units
          .filter((unit) => unit.parentId === params.instId)
          .map((unit) => unit.type),
      ),
    ];
    if (
      currentInstUnitTypes.length &&
      !currentInstUnitTypes.includes(selectedUnitType.type)
    ) {
      const newUnitType = currentInstUnitTypes[0];
      this.selectedUnitType.next(unitTypes.find((u) => u.type === newUnitType));
    }
  }
}
